// src/components/LoginForm.js
'use strict';

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import {
  Login as LoginIcon,
  Apartment as TenantIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  VerifiedUser as VerifiedUserIcon,
  BusinessRounded as BusinessRoundedIcon,
  StorefrontRounded as StorefrontRoundedIcon,
  AdminPanelSettingsRounded as AdminPanelSettingsRoundedIcon,
  SearchRounded as SearchRoundedIcon,
  CheckCircleRounded as CheckCircleRoundedIcon,
  ArrowForwardRounded as ArrowForwardRoundedIcon,
} from '@mui/icons-material';

import api from '../../api/api';
import backgroundImage from '../assets/integranube-background.jpg';
import logoHorizontal from '../assets/logo.png';

const AUTH_EVENT_KEY = 'integranube_auth_event';

const TURNSTILE_SCRIPT_ID = 'integranube-turnstile-script';
const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TURNSTILE_SCRIPT_TIMEOUT_MS = 12000;
const TURNSTILE_RETRY_INTERVAL_MS = 5000;

let turnstileScriptPromise = null;

function clearTurnstileScriptElement() {
  try {
    const script = document.getElementById(TURNSTILE_SCRIPT_ID);
    script?.remove?.();
  } catch (_) {}
}

function loadTurnstileScript({ forceReload = false } = {}) {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('TURNSTILE_BROWSER_REQUIRED'));
  }

  // Si la API ya está disponible, no recargamos el script innecesariamente.
  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }

  if (forceReload) {
    turnstileScriptPromise = null;
    clearTurnstileScriptElement();
  }

  if (turnstileScriptPromise) {
    return turnstileScriptPromise;
  }

  turnstileScriptPromise = new Promise((resolve, reject) => {
    let settled = false;
    let timeoutId = null;

    const cleanup = () => {
      if (timeoutId) window.clearTimeout(timeoutId);
    };

    const succeed = () => {
      if (settled) return;

      if (!window.turnstile) {
        fail('TURNSTILE_API_NOT_AVAILABLE');
        return;
      }

      settled = true;
      cleanup();
      resolve(window.turnstile);
    };

    const fail = (code) => {
      if (settled) return;
      settled = true;
      cleanup();
      turnstileScriptPromise = null;
      reject(new Error(code));
    };

    let script = document.getElementById(TURNSTILE_SCRIPT_ID);

    // Un script previo puede haber quedado insertado pero fallado. Si no existe
    // la API global, lo recreamos para evitar una Promise colgada indefinidamente.
    if (script?.dataset?.failed === '1') {
      clearTurnstileScriptElement();
      script = null;
    }

    if (!script) {
      script = document.createElement('script');
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = TURNSTILE_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      document.head.appendChild(script);
    }

    script.addEventListener('load', succeed, { once: true });
    script.addEventListener(
      'error',
      () => {
        script.dataset.failed = '1';
        fail('TURNSTILE_SCRIPT_LOAD_FAILED');
      },
      { once: true }
    );

    timeoutId = window.setTimeout(() => {
      if (window.turnstile) {
        succeed();
        return;
      }

      try {
        script.dataset.failed = '1';
      } catch (_) {}

      fail('TURNSTILE_SCRIPT_TIMEOUT');
    }, TURNSTILE_SCRIPT_TIMEOUT_MS);

    // Cubre el caso en que el script terminó de cargar justo antes de registrar
    // los listeners.
    if (window.turnstile) succeed();
  });

  return turnstileScriptPromise;
}

function classifyTurnstileError(rawCode) {
  const code = String(rawCode || 'unknown').trim();

  if (
    code === 'TURNSTILE_SCRIPT_LOAD_FAILED' ||
    code === 'TURNSTILE_SCRIPT_TIMEOUT' ||
    code === 'TURNSTILE_API_NOT_AVAILABLE' ||
    code === '200500'
  ) {
    return {
      code,
      retryable: true,
      kind: 'network',
      message:
        'No pudimos cargar la verificación de seguridad. Revisaremos la conexión y reintentaremos automáticamente.',
    };
  }

  if (
    code === '110600' ||
    code === '110620' ||
    code.startsWith('300') ||
    code.startsWith('600')
  ) {
    return {
      code,
      retryable: true,
      kind: 'temporary',
      message:
        'La verificación de seguridad tuvo un problema temporal. Estamos reintentando.',
    };
  }

  if (
    code === '110100' ||
    code === '110110' ||
    code === '110200' ||
    code === '400020' ||
    code === '400021' ||
    code === '400070'
  ) {
    return {
      code,
      retryable: false,
      kind: 'configuration',
      message:
        'La verificación de seguridad no está disponible por un problema de configuración. Contacta al administrador.',
    };
  }

  if (code === '200100') {
    return {
      code,
      retryable: false,
      kind: 'clock',
      message:
        'No pudimos validar la seguridad del navegador. Comprueba la fecha y hora del equipo e intenta nuevamente.',
    };
  }

  if (code === 'UNSUPPORTED_BROWSER') {
    return {
      code,
      retryable: false,
      kind: 'browser',
      message:
        'Este navegador no es compatible con la verificación de seguridad. Actualízalo o utiliza otro navegador.',
    };
  }

  return {
    code,
    retryable: true,
    kind: 'unknown',
    message:
      'No pudimos completar la verificación de seguridad. Puedes reintentarlo sin recargar la página.',
  };
}

const TurnstileWidget = forwardRef(function TurnstileWidget(
  {
    siteKey,
    action = 'login',
    theme = 'auto',
    onToken,
    onExpired,
    onTimeout,
    onError,
    onReady,
    onLoading,
  },
  ref
) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const retryTimerRef = useRef(null);
  const remountRef = useRef(null);

  const callbacksRef = useRef({
    onToken,
    onExpired,
    onTimeout,
    onError,
    onReady,
    onLoading,
  });

  useEffect(() => {
    callbacksRef.current = {
      onToken,
      onExpired,
      onTimeout,
      onError,
      onReady,
      onLoading,
    };
  }, [onToken, onExpired, onTimeout, onError, onReady, onLoading]);

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const resetWidget = useCallback(() => {
    clearRetryTimer();

    try {
      const widgetId = widgetIdRef.current;

      if (
        widgetId != null &&
        typeof window !== 'undefined' &&
        window.turnstile
      ) {
        callbacksRef.current.onLoading?.('retrying');
        window.turnstile.reset(widgetId);
        return true;
      }
    } catch (_) {}

    return false;
  }, [clearRetryTimer]);

  useImperativeHandle(
    ref,
    () => ({
      reset() {
        if (!resetWidget()) {
          remountRef.current?.({ forceScriptReload: true });
        }
      },

      reload() {
        remountRef.current?.({ forceScriptReload: true });
      },

      getValue() {
        try {
          const widgetId = widgetIdRef.current;

          if (
            widgetId == null ||
            typeof window === 'undefined' ||
            !window.turnstile
          ) {
            return '';
          }

          return String(window.turnstile.getResponse(widgetId) || '').trim();
        } catch (_) {
          return '';
        }
      },
    }),
    [resetWidget]
  );

  useEffect(() => {
    let cancelled = false;
    let generation = 0;

    const removeCurrentWidget = () => {
      clearRetryTimer();

      try {
        const widgetId = widgetIdRef.current;

        if (
          widgetId != null &&
          typeof window !== 'undefined' &&
          window.turnstile
        ) {
          window.turnstile.remove(widgetId);
        }
      } catch (_) {
        // El desmontaje nunca debe bloquear el formulario.
      } finally {
        widgetIdRef.current = null;
      }
    };

    const mountWidget = async ({ forceScriptReload = false } = {}) => {
      const currentGeneration = ++generation;
      callbacksRef.current.onLoading?.('loading');

      try {
        const turnstile = await loadTurnstileScript({ forceReload: forceScriptReload });

        if (
          cancelled ||
          currentGeneration !== generation ||
          !containerRef.current
        ) {
          return;
        }

        removeCurrentWidget();
        containerRef.current.replaceChildren();

        const widgetId = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          theme,
          language: 'es',
          size: 'flexible',
          retry: 'auto',
          'retry-interval': TURNSTILE_RETRY_INTERVAL_MS,
          'refresh-expired': 'auto',
          'refresh-timeout': 'auto',
          appearance: 'always',
          'response-field': false,
          callback: (value) => {
            clearRetryTimer();
            callbacksRef.current.onToken?.(String(value || '').trim());
          },
          'expired-callback': () => {
            callbacksRef.current.onExpired?.();
          },
          'timeout-callback': () => {
            callbacksRef.current.onTimeout?.();
          },
          'unsupported-callback': () => {
            callbacksRef.current.onError?.('UNSUPPORTED_BROWSER');
          },
          'error-callback': (code) => {
            callbacksRef.current.onError?.(String(code || 'unknown'));

            // false permite que Turnstile conserve su recuperación automática.
            return false;
          },
        });

        widgetIdRef.current = widgetId;
        callbacksRef.current.onReady?.();
      } catch (err) {
        if (!cancelled && currentGeneration === generation) {
          callbacksRef.current.onError?.(
            err?.message || 'TURNSTILE_SCRIPT_LOAD_FAILED'
          );
        }
      }
    };

    remountRef.current = mountWidget;
    mountWidget();

    return () => {
      cancelled = true;
      generation += 1;
      remountRef.current = null;
      removeCurrentWidget();
    };
  }, [siteKey, action, theme, clearRetryTimer]);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        minHeight: 65,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    />
  );
});

const CLOUDFLARE_COLO_MAP = {
  SCL: {
    city: 'Santiago',
    region: 'RM',
    country: 'Chile',
    countryCode: 'CL',
    flag: '🇨🇱',
  },
  VAP: {
    city: 'Valparaíso',
    region: 'Valparaíso',
    country: 'Chile',
    countryCode: 'CL',
    flag: '🇨🇱',
  },
  EZE: {
    city: 'Buenos Aires',
    region: 'Buenos Aires',
    country: 'Argentina',
    countryCode: 'AR',
    flag: '🇦🇷',
  },
  LIM: {
    city: 'Lima',
    region: 'Lima',
    country: 'Perú',
    countryCode: 'PE',
    flag: '🇵🇪',
  },
  GRU: {
    city: 'São Paulo',
    region: 'São Paulo',
    country: 'Brasil',
    countryCode: 'BR',
    flag: '🇧🇷',
  },
  GIG: {
    city: 'Río de Janeiro',
    region: 'Río de Janeiro',
    country: 'Brasil',
    countryCode: 'BR',
    flag: '🇧🇷',
  },
  BOG: {
    city: 'Bogotá',
    region: 'Bogotá',
    country: 'Colombia',
    countryCode: 'CO',
    flag: '🇨🇴',
  },
  MDE: {
    city: 'Medellín',
    region: 'Antioquia',
    country: 'Colombia',
    countryCode: 'CO',
    flag: '🇨🇴',
  },
  UIO: {
    city: 'Quito',
    region: 'Pichincha',
    country: 'Ecuador',
    countryCode: 'EC',
    flag: '🇪🇨',
  },
  GYE: {
    city: 'Guayaquil',
    region: 'Guayas',
    country: 'Ecuador',
    countryCode: 'EC',
    flag: '🇪🇨',
  },
  MEX: {
    city: 'Ciudad de México',
    region: 'CDMX',
    country: 'México',
    countryCode: 'MX',
    flag: '🇲🇽',
  },
  QRO: {
    city: 'Querétaro',
    region: 'Querétaro',
    country: 'México',
    countryCode: 'MX',
    flag: '🇲🇽',
  },
  MIA: {
    city: 'Miami',
    region: 'Florida',
    country: 'Estados Unidos',
    countryCode: 'US',
    flag: '🇺🇸',
  },
  IAD: {
    city: 'Washington DC',
    region: 'Virginia',
    country: 'Estados Unidos',
    countryCode: 'US',
    flag: '🇺🇸',
  },
  DCA: {
    city: 'Washington DC',
    region: 'DC',
    country: 'Estados Unidos',
    countryCode: 'US',
    flag: '🇺🇸',
  },
  ATL: {
    city: 'Atlanta',
    region: 'Georgia',
    country: 'Estados Unidos',
    countryCode: 'US',
    flag: '🇺🇸',
  },
  ORD: {
    city: 'Chicago',
    region: 'Illinois',
    country: 'Estados Unidos',
    countryCode: 'US',
    flag: '🇺🇸',
  },
  DFW: {
    city: 'Dallas',
    region: 'Texas',
    country: 'Estados Unidos',
    countryCode: 'US',
    flag: '🇺🇸',
  },
  LAX: {
    city: 'Los Ángeles',
    region: 'California',
    country: 'Estados Unidos',
    countryCode: 'US',
    flag: '🇺🇸',
  },
  SFO: {
    city: 'San Francisco',
    region: 'California',
    country: 'Estados Unidos',
    countryCode: 'US',
    flag: '🇺🇸',
  },
  SJC: {
    city: 'San José',
    region: 'California',
    country: 'Estados Unidos',
    countryCode: 'US',
    flag: '🇺🇸',
  },
  SEA: {
    city: 'Seattle',
    region: 'Washington',
    country: 'Estados Unidos',
    countryCode: 'US',
    flag: '🇺🇸',
  },
  JFK: {
    city: 'Nueva York',
    region: 'New York',
    country: 'Estados Unidos',
    countryCode: 'US',
    flag: '🇺🇸',
  },
  EWR: {
    city: 'Newark',
    region: 'New Jersey',
    country: 'Estados Unidos',
    countryCode: 'US',
    flag: '🇺🇸',
  },
  BOS: {
    city: 'Boston',
    region: 'Massachusetts',
    country: 'Estados Unidos',
    countryCode: 'US',
    flag: '🇺🇸',
  },
  DEN: {
    city: 'Denver',
    region: 'Colorado',
    country: 'Estados Unidos',
    countryCode: 'US',
    flag: '🇺🇸',
  },
  LAS: {
    city: 'Las Vegas',
    region: 'Nevada',
    country: 'Estados Unidos',
    countryCode: 'US',
    flag: '🇺🇸',
  },
  PHX: {
    city: 'Phoenix',
    region: 'Arizona',
    country: 'Estados Unidos',
    countryCode: 'US',
    flag: '🇺🇸',
  },
  PDX: {
    city: 'Portland',
    region: 'Oregon',
    country: 'Estados Unidos',
    countryCode: 'US',
    flag: '🇺🇸',
  },
  MSP: {
    city: 'Minneapolis',
    region: 'Minnesota',
    country: 'Estados Unidos',
    countryCode: 'US',
    flag: '🇺🇸',
  },
  DTW: {
    city: 'Detroit',
    region: 'Michigan',
    country: 'Estados Unidos',
    countryCode: 'US',
    flag: '🇺🇸',
  },
  CLT: {
    city: 'Charlotte',
    region: 'North Carolina',
    country: 'Estados Unidos',
    countryCode: 'US',
    flag: '🇺🇸',
  },
  MAD: {
    city: 'Madrid',
    region: 'Madrid',
    country: 'España',
    countryCode: 'ES',
    flag: '🇪🇸',
  },
  BCN: {
    city: 'Barcelona',
    region: 'Cataluña',
    country: 'España',
    countryCode: 'ES',
    flag: '🇪🇸',
  },
  CDG: {
    city: 'París',
    region: 'Île-de-France',
    country: 'Francia',
    countryCode: 'FR',
    flag: '🇫🇷',
  },
  LHR: {
    city: 'Londres',
    region: 'Inglaterra',
    country: 'Reino Unido',
    countryCode: 'GB',
    flag: '🇬🇧',
  },
  AMS: {
    city: 'Ámsterdam',
    region: 'Holanda Septentrional',
    country: 'Países Bajos',
    countryCode: 'NL',
    flag: '🇳🇱',
  },
  FRA: {
    city: 'Frankfurt',
    region: 'Hesse',
    country: 'Alemania',
    countryCode: 'DE',
    flag: '🇩🇪',
  },
};

function parseCloudflareTrace(text) {
  const out = {};

  String(text || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line) => {
      const idx = line.indexOf('=');
      if (idx <= 0) return;

      const key = line.slice(0, idx).trim();
      const value = line.slice(idx + 1).trim();

      if (key) out[key] = value;
    });

  return out;
}

function isLocalHost(host) {
  const value = String(host || '').toLowerCase();

  return (
    value === 'localhost' ||
    value === '127.0.0.1' ||
    value === '0.0.0.0' ||
    value.startsWith('192.168.') ||
    value.startsWith('10.') ||
    value.endsWith('.local')
  );
}

function resolveInitialRuntimeLocation(hostname) {
  const host = String(hostname || '').toLowerCase();

  const cloudflarePagesHosts = [
    'gestion.integranube.cl',
    'pos.integranube.cl',
    'webapp.integranube.cl',
    'erp.integranube.cl',
  ];

  if (
    cloudflarePagesHosts.includes(host) ||
    host.endsWith('.pages.dev')
  ) {
    return {
      ready: false,
      source: 'cloudflare_pages',
      badge: 'CF Pages',
      label: 'Cloudflare Pages',
      provider: 'Cloudflare Pages',
      flag: '🌐',
      city: 'Detectando edge',
      region: '',
      country: 'Cloudflare',
      countryCode: '',
      colo: '',
      host,
      detail: 'Detectando ubicación mediante /cdn-cgi/trace',
    };
  }

  if (isLocalHost(host)) {
    return {
      ready: true,
      source: 'local',
      badge: 'LOCAL',
      label: 'Desarrollo local',
      provider: 'Localhost',
      flag: '💻',
      city: 'Localhost',
      region: '',
      country: 'Equipo local',
      countryCode: '',
      colo: '',
      host,
      detail: 'Entorno local',
    };
  }

  return {
    ready: true,
    source: 'unknown',
    badge: 'UNKNOWN',
    label: 'Origen no identificado',
    provider: 'Host no reconocido',
    flag: '🌐',
    city: 'Origen desconocido',
    region: '',
    country: '',
    countryCode: '',
    colo: '',
    host,
    detail: `Host no reconocido: ${host || 'sin host'}`,
  };
}

function resolveCloudflareRuntimeLocation(hostname, trace) {
  const host = String(hostname || '').toLowerCase();
  const colo = String(trace?.colo || '').trim().toUpperCase();
  const visitorCountry = String(trace?.loc || '').trim().toUpperCase();
  const mapped = CLOUDFLARE_COLO_MAP[colo];

  if (mapped) {
    return {
      ready: true,
      source: 'cloudflare_pages',
      badge: 'CF Pages',
      label: 'Cloudflare Pages',
      provider: 'Cloudflare Pages',
      flag: mapped.flag,
      city: mapped.city,
      region: mapped.region,
      country: mapped.country,
      countryCode: mapped.countryCode,
      colo,
      visitorCountry,
      host,
      detail: `Edge Cloudflare ${colo} · visitante ${
        visitorCountry || 'N/D'
      }`,
    };
  }

  if (colo) {
    return {
      ready: true,
      source: 'cloudflare_pages',
      badge: 'CF Pages',
      label: 'Cloudflare Pages',
      provider: 'Cloudflare Pages',
      flag: '🌐',
      city: `Cloudflare ${colo}`,
      region: '',
      country: 'Edge Cloudflare',
      countryCode: '',
      colo,
      visitorCountry,
      host,
      detail: `Edge Cloudflare ${colo} · ubicación no mapeada`,
    };
  }

  return {
    ready: true,
    source: 'cloudflare_pages',
    badge: 'CF Pages',
    label: 'Cloudflare Pages',
    provider: 'Cloudflare Pages',
    flag: '🌐',
    city: 'Cloudflare Edge',
    region: '',
    country: 'Global',
    countryCode: '',
    colo: '',
    visitorCountry,
    host,
    detail: 'No se pudo leer colo desde /cdn-cgi/trace',
  };
}

function ServedLocationFlag() {
  const theme = useTheme();

  const [runtime, setRuntime] = useState(() => {
    const host =
      typeof window !== 'undefined' ? window.location.hostname : '';

    return resolveInitialRuntimeLocation(host);
  });

  useEffect(() => {
    let alive = true;

    const host =
      typeof window !== 'undefined' ? window.location.hostname : '';

    const initial = resolveInitialRuntimeLocation(host);

    setRuntime(initial);

    if (initial.source !== 'cloudflare_pages') {
      return () => {
        alive = false;
      };
    }

    const detectCloudflareEdge = async () => {
      try {
        const resp = await fetch('/cdn-cgi/trace', {
          method: 'GET',
          cache: 'no-store',
        });

        if (!resp.ok) {
          throw new Error(`CF_TRACE_HTTP_${resp.status}`);
        }

        const text = await resp.text();
        const trace = parseCloudflareTrace(text);
        const resolved = resolveCloudflareRuntimeLocation(host, trace);

        if (alive) {
          setRuntime(resolved);
        }
      } catch (_) {
        if (alive) {
          setRuntime({
            ...initial,
            ready: true,
            flag: '🌐',
            city: 'Cloudflare Pages',
            country: 'Global',
            detail: 'No fue posible consultar /cdn-cgi/trace',
          });
        }
      }
    };

    detectCloudflareEdge();

    return () => {
      alive = false;
    };
  }, []);

  if (!runtime) return null;

  const isChileServer = runtime.source === 'chile_server';
  const isCloudflarePages = runtime.source === 'cloudflare_pages';
  const isLocal = runtime.source === 'local';

  const bgColor = isChileServer
    ? '#0f766e'
    : isCloudflarePages
      ? '#f97316'
      : isLocal
        ? '#d97706'
        : '#475569';

  const titleParts = [
    runtime.label,
    runtime.city,
    runtime.region,
    runtime.country,
    runtime.colo ? `colo=${runtime.colo}` : '',
    runtime.host ? `host=${runtime.host}` : '',
    runtime.detail,
  ].filter(Boolean);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: { xs: 8, sm: 12 },
        right: { xs: 8, sm: 12 },
        zIndex: 9999,
        px: { xs: 1.05, sm: 1.25 },
        py: 0.65,
        borderRadius: 999,
        color: '#fff',
        backgroundColor: bgColor,
        boxShadow: '0 10px 30px rgba(0,0,0,0.28)',
        border: '1px solid rgba(255,255,255,0.28)',
        backdropFilter: 'blur(10px)',
        maxWidth: { xs: 'calc(100vw - 16px)', sm: 360 },
        cursor: 'default',
        userSelect: 'none',
      }}
      title={titleParts.join(' · ')}
    >
      <Stack direction="row" spacing={0.85} alignItems="center">
        <Box
          component="span"
          sx={{
            fontSize: { xs: 15, sm: 16 },
            lineHeight: 1,
            filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.18))',
          }}
        >
          {runtime.flag}
        </Box>

        <Stack spacing={0} sx={{ minWidth: 0 }}>
          <Typography
            component="span"
            sx={{
              display: 'block',
              fontSize: { xs: 10.5, sm: 11 },
              fontWeight: 950,
              letterSpacing: 0.35,
              lineHeight: 1.12,
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: { xs: 190, sm: 280 },
            }}
          >
            {runtime.ready ? runtime.city : 'Detectando ubicación'}
            {runtime.country ? ` · ${runtime.country}` : ''}
          </Typography>

          <Typography
            component="span"
            sx={{
              display: 'block',
              fontSize: { xs: 9.5, sm: 10 },
              fontWeight: 850,
              letterSpacing: 0.25,
              lineHeight: 1.15,
              color: alpha(theme.palette.common.white, 0.82),
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: { xs: 190, sm: 280 },
            }}
          >
            {runtime.badge}
            {runtime.colo ? ` · ${runtime.colo}` : ''}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}

function safeJsonParse(value, fallback = null) {
  try {
    if (!value) return fallback;
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function normalizeText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function safeUserLabel(u) {
  if (!u) return '';

  const name =
    u.razon_social ||
    u.nombre ||
    u.name ||
    u.nombre_completo ||
    u.full_name ||
    [u.nombres, u.apellidos].filter(Boolean).join(' ') ||
    u.email ||
    u.rut ||
    'Usuario';

  return String(name);
}

function getTenantId(t) {
  return (
    t?.tenantid ||
    t?.tenant_id ||
    t?.id_tenant ||
    t?.idTenant ||
    t?.id ||
    ''
  );
}

function getTenantRole(t) {
  return (
    t?.role ||
    t?.rol ||
    t?.perfil ||
    t?.tipo_usuario ||
    t?.user_role ||
    t?.tipo ||
    ''
  );
}

function getTenantName(t, fallback = 'Empresa') {
  return (
    t?.name ||
    t?.nombre ||
    t?.nombre_fantasia ||
    t?.razon_social ||
    t?.tenant_name ||
    t?.empresa ||
    fallback
  );
}

function getTenantLegalName(t) {
  const main = getTenantName(t, '');
  const legal =
    t?.razon_social ||
    t?.legal_name ||
    t?.nombre_legal ||
    t?.empresa_razon_social ||
    '';

  if (!legal || normalizeText(legal) === normalizeText(main)) return '';
  return legal;
}

function getTenantRut(t) {
  return (
    t?.rut ||
    t?.rut_empresa ||
    t?.company_rut ||
    t?.rut_tenant ||
    ''
  );
}

function getTenantStatus(t) {
  return t?.status || t?.estado || t?.state || '';
}

function getTenantInitials(t) {
  const name = getTenantName(t, 'Empresa');
  const words = String(name)
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!words.length) return 'IN';

  const first = words[0]?.[0] || '';
  const second =
    words.length > 1 ? words[1]?.[0] : words[0]?.[1] || '';

  return `${first}${second}`.toUpperCase();
}

function normalizeTenant(t, index = 0) {
  const tenantid = getTenantId(t);

  return {
    ...t,
    tenantid,
    _index: index,
    _name: getTenantName(t, `Empresa ${index + 1}`),
    _legalName: getTenantLegalName(t),
    _rut: getTenantRut(t),
    _role: getTenantRole(t),
    _status: getTenantStatus(t),
    _initials: getTenantInitials(t),
  };
}

/**
 * Login compatible con correo o RUT.
 *
 * Entrada aceptada:
 * - admin@integranube.cl
 * - 16.717.958-4
 * - 16717958-4
 * - 167179584
 *
 * Salida:
 * - email en minúsculas
 * - RUT sin puntos y con guion: 16717958-4
 */
function normalizeLoginIdentifier(value) {
  const raw = String(value || '').trim();

  if (!raw) return '';

  const upper = raw.toUpperCase();

  const rutCompact = upper
    .replace(/\./g, '')
    .replace(/-/g, '')
    .replace(/\s+/g, '')
    .replace(/[^0-9K]/g, '');

  const looksLikeRut =
    !raw.includes('@') &&
    /^[0-9]{7,8}[0-9K]$/.test(rutCompact);

  if (looksLikeRut) {
    const body = rutCompact.slice(0, -1).replace(/^0+/, '') || '0';
    const dv = rutCompact.slice(-1);

    return `${body}-${dv}`;
  }

  return raw.toLowerCase();
}

function displayLoginIdentifier(value) {
  return String(value || '').trim();
}

const waitForBrowserAutofill = () =>
  new Promise((resolve) => {
    requestAnimationFrame(() => {
      setTimeout(resolve, 0);
    });
  });

function joinUrl(base, path) {
  const cleanBase = String(base || '').replace(/\/+$/, '');
  const cleanPath = String(path || '').replace(/^\/+/, '');

  if (!cleanBase) return `/${cleanPath}`;
  return `${cleanBase}/${cleanPath}`;
}

function resolveApiUrl(path) {
  try {
    if (typeof api.getUri === 'function') {
      const uri = api.getUri({ url: path });
      if (uri) return uri;
    }
  } catch (_) {}

  const baseURL =
    api?.defaults?.baseURL ||
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_API_URL ||
    '';

  return joinUrl(baseURL, path);
}

async function fetchLoginDirect(payload) {
  const url = resolveApiUrl('/auth/login');

  let resp;

  try {
    resp = await fetch(url, {
      method: 'POST',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload || {}),
    });
  } catch (cause) {
    const err = new Error('AUTH_LOGIN_NETWORK_ERROR');
    err.cause = cause;
    throw err;
  }

  const raw = await resp.text();

  let data = null;

  try {
    data = raw ? JSON.parse(raw) : null;
  } catch (_) {
    data = { raw };
  }

  if (!resp.ok) {
    const err = new Error(`AUTH_LOGIN_FAILED_${resp.status}`);
    err.status = resp.status;
    err.data = data;
    throw err;
  }

  return data;
}

async function fetchAuthMeWithToken(authToken) {
  if (!authToken) {
    throw new Error('AUTH_TOKEN_REQUIRED');
  }

  const url = resolveApiUrl('/auth/me');

  const resp = await fetch(url, {
    method: 'GET',
    cache: 'no-store',
    headers: {
      Authorization: `Bearer ${authToken}`,
      Accept: 'application/json',
    },
  });

  const raw = await resp.text();

  let data = null;

  try {
    data = raw ? JSON.parse(raw) : null;
  } catch (_) {
    data = { raw };
  }

  if (!resp.ok) {
    const err = new Error(`AUTH_ME_FAILED_${resp.status}`);
    err.status = resp.status;
    err.data = data;
    throw err;
  }

  return data;
}

export default function LoginForm({ onLogin }) {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const switchTenantMode = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('switchTenant') === '1';
  }, [location.search]);

  const TURNSTILE_SITE_KEY =
    process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

  const turnstileEnabled = Boolean(TURNSTILE_SITE_KEY);

  const turnstileRef = useRef(null);
  const turnstileAutoRetryRef = useRef(0);
  const turnstileAutoRetryTimerRef = useRef(null);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileReady, setTurnstileReady] = useState(false);
  const [turnstileStatus, setTurnstileStatus] = useState(
    turnstileEnabled ? 'loading' : 'disabled'
  );
  const [turnstileIssue, setTurnstileIssue] = useState(null);

  const [initialLogin] = useState(() => {
    try {
      return (
        localStorage.getItem('rememberedLogin') ||
        localStorage.getItem('rememberedEmail') ||
        ''
      );
    } catch {
      return '';
    }
  });

  const [rememberMe, setRememberMe] = useState(() => !!initialLogin);

  const loginInputRef = useRef(null);
  const passInputRef = useRef(null);
  const formRef = useRef(null);

  const loginValueRef = useRef(initialLogin);
  const passValueRef = useRef('');

  const submitLockRef = useRef(false);

  const [step, setStep] = useState('login');
  const [loading, setLoading] = useState(false);
  const [loadingTenantId, setLoadingTenantId] = useState('');
  const [error, setError] = useState('');

  const [token, setToken] = useState('');
  const [user, setUser] = useState(null);

  const [tenants, setTenants] = useState([]);
  const [selectedTenantObj, setSelectedTenantObj] = useState(null);
  const [tenantSearch, setTenantSearch] = useState('');

  const tenantOptions = useMemo(() => {
    return (tenants || [])
      .map((t, index) => normalizeTenant(t, index))
      .filter((t) => t.tenantid);
  }, [tenants]);

  const filteredTenantOptions = useMemo(() => {
    const q = normalizeText(tenantSearch);

    if (!q) return tenantOptions;

    return tenantOptions.filter((t) => {
      const haystack = normalizeText(
        [
          t._name,
          t._legalName,
          t._rut,
          t._role,
          t._status,
          t.tenantid,
        ]
          .filter(Boolean)
          .join(' ')
      );

      return haystack.includes(q);
    });
  }, [tenantOptions, tenantSearch]);

  const clearTurnstileAutoRetry = useCallback(() => {
    if (turnstileAutoRetryTimerRef.current) {
      window.clearTimeout(turnstileAutoRetryTimerRef.current);
      turnstileAutoRetryTimerRef.current = null;
    }
  }, []);

  const resetTurnstile = useCallback(() => {
    if (!turnstileEnabled) return;

    clearTurnstileAutoRetry();
    setTurnstileToken('');
    setTurnstileIssue(null);
    setTurnstileStatus('loading');

    try {
      turnstileRef.current?.reset?.();
    } catch (_) {}
  }, [turnstileEnabled, clearTurnstileAutoRetry]);

  const retryTurnstileNow = useCallback(() => {
    if (!turnstileEnabled) return;

    clearTurnstileAutoRetry();
    turnstileAutoRetryRef.current = 0;
    setTurnstileToken('');
    setTurnstileIssue(null);
    setTurnstileReady(false);
    setTurnstileStatus('loading');

    try {
      turnstileRef.current?.reload?.();
    } catch (_) {
      try {
        turnstileRef.current?.reset?.();
      } catch (_) {}
    }
  }, [turnstileEnabled, clearTurnstileAutoRetry]);

  useEffect(() => {
    return () => clearTurnstileAutoRetry();
  }, [clearTurnstileAutoRetry]);

  const getCurrentTurnstileToken = useCallback(() => {
    if (!turnstileEnabled) return '';

    const fromState = String(turnstileToken || '').trim();
    if (fromState) return fromState;

    try {
      return String(
        turnstileRef.current?.getValue?.() || ''
      ).trim();
    } catch (_) {
      return '';
    }
  }, [turnstileEnabled, turnstileToken]);

  const commitAuthSession = useCallback(
    ({ token: authToken, user: authUser, tenant }) => {
      if (!authToken) throw new Error('AUTH_TOKEN_REQUIRED');

      const normalizedTenant = normalizeTenant(tenant);

      if (!normalizedTenant?.tenantid) {
        throw new Error('TENANT_REQUIRED');
      }

      api.defaults.headers.common.Authorization = `Bearer ${authToken}`;
      api.defaults.headers.common.tenantid = normalizedTenant.tenantid;

      localStorage.setItem('tenant', JSON.stringify(normalizedTenant));
      localStorage.setItem('user', JSON.stringify(authUser || null));
      localStorage.setItem('tenantid', normalizedTenant.tenantid);
      localStorage.setItem('token', authToken);

      localStorage.setItem(
        AUTH_EVENT_KEY,
        JSON.stringify({
          type: 'login',
          at: Date.now(),
          tenantid: normalizedTenant.tenantid,
        })
      );
    },
    []
  );

  const clearAuth = useCallback((opts = {}) => {
    const { clearStorage = false } = opts;

    try {
      delete api.defaults.headers.common.Authorization;
      delete api.defaults.headers.common.tenantid;
    } catch (_) {}

    if (clearStorage) {
      try {
        localStorage.removeItem('token');
        localStorage.removeItem('tenantid');
        localStorage.removeItem('tenant');
        localStorage.removeItem('user');

        localStorage.setItem(
          AUTH_EVENT_KEY,
          JSON.stringify({
            type: 'logout',
            at: Date.now(),
          })
        );
      } catch (_) {}
    }

    setToken('');
    setUser(null);
    setTenants([]);
    setSelectedTenantObj(null);
    setTenantSearch('');
  }, []);

  const syncCredentialRefs = useCallback((formEl = null) => {
    let formLogin = '';
    let formPassword = '';

    try {
      const targetForm = formEl || formRef.current;

      if (targetForm) {
        const fd = new FormData(targetForm);

        formLogin = String(fd.get('email') || '').trim();
        formPassword = String(fd.get('password') || '');
      }
    } catch (_) {}

    const domLogin = String(loginInputRef.current?.value ?? '').trim();
    const domPassword = String(passInputRef.current?.value ?? '');

    const finalLogin = String(
      formLogin || domLogin || loginValueRef.current || ''
    ).trim();

    const finalPassword = String(
      formPassword || domPassword || passValueRef.current || ''
    );

    loginValueRef.current = finalLogin;
    passValueRef.current = finalPassword;

    return {
      loginIdentifier: finalLogin,
      password: finalPassword,
    };
  }, []);

  const getLiveCredentials = useCallback(
    (formEl = null) => syncCredentialRefs(formEl),
    [syncCredentialRefs]
  );

  useEffect(() => {
    if (step !== 'login') return;

    const runSync = () => syncCredentialRefs();

    const raf = requestAnimationFrame(runSync);
    const t1 = setTimeout(runSync, 120);
    const t2 = setTimeout(runSync, 400);
    const t3 = setTimeout(runSync, 900);
    const t4 = setTimeout(runSync, 1400);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, [step, syncCredentialRefs]);

  useEffect(() => {
    if (!switchTenantMode) return;

    let alive = true;

    const loadTenantsFromCurrentSession = async () => {
      const storedToken = localStorage.getItem('token');

      if (!storedToken) {
        setStep('login');
        return;
      }

      setLoading(true);
      setError('');
      setTenantSearch('');
      setSelectedTenantObj(null);

      try {
        try {
          localStorage.removeItem('tenant');
          localStorage.removeItem('tenantid');

          delete api.defaults.headers.common.tenantid;
        } catch (_) {}

        api.defaults.headers.common.Authorization = `Bearer ${storedToken}`;

        const meData = await fetchAuthMeWithToken(storedToken);

        if (!alive) return;

        const allowed = Array.isArray(meData?.allowedTenants)
          ? meData.allowedTenants
          : [];

        const meUser =
          meData?.user ||
          safeJsonParse(localStorage.getItem('user'), null);

        if (!allowed.length) {
          setStep('login');
          setError('Tu usuario no tiene empresas asignadas.');
          return;
        }

        loginValueRef.current =
          meUser?.email ||
          meUser?.rut ||
          localStorage.getItem('rememberedLogin') ||
          localStorage.getItem('rememberedEmail') ||
          '';

        setToken(storedToken);
        setUser(meUser);
        setTenants(allowed);
        setStep('tenant');
      } catch (err) {
        console.error(
          '[LoginForm] No fue posible cargar tenants:',
          err
        );

        clearAuth({ clearStorage: true });
        resetTurnstile();

        setStep('login');
        setError('La sesión expiró. Ingresa nuevamente.');
        navigate('/login', { replace: true });
      } finally {
        if (alive) setLoading(false);
      }
    };

    loadTenantsFromCurrentSession();

    return () => {
      alive = false;
    };
  }, [
    switchTenantMode,
    clearAuth,
    resetTurnstile,
    navigate,
  ]);

  const goToLogin = useCallback(
    (opts = {}) => {
      const { clearStoredSession = false } = opts;

      setError('');
      setLoading(false);
      setLoadingTenantId('');
      setStep('login');
      setSelectedTenantObj(null);
      setTenantSearch('');
      passValueRef.current = '';
      submitLockRef.current = false;

      if (passInputRef.current) {
        passInputRef.current.value = '';
      }

      clearAuth({ clearStorage: clearStoredSession });
      resetTurnstile();

      if (clearStoredSession || switchTenantMode) {
        navigate('/login', { replace: true });
      }

      requestAnimationFrame(() => {
        syncCredentialRefs();
        loginInputRef.current?.focus?.();
      });
    },
    [
      clearAuth,
      resetTurnstile,
      syncCredentialRefs,
      navigate,
      switchTenantMode,
    ]
  );

  const handleLoginSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (submitLockRef.current) return;
      submitLockRef.current = true;

      const formEl = e.currentTarget;

      let { loginIdentifier, password } =
        getLiveCredentials(formEl);

      await waitForBrowserAutofill();

      ({ loginIdentifier, password } =
        getLiveCredentials(formEl));

      const normalizedLoginIdentifier =
        normalizeLoginIdentifier(loginIdentifier);

      const currentTurnstileToken =
        getCurrentTurnstileToken();

      if (!normalizedLoginIdentifier || !password) {
        submitLockRef.current = false;

        setError('Ingresa correo o RUT y contraseña.');

        if (!normalizedLoginIdentifier) {
          loginInputRef.current?.focus?.();
        } else {
          passInputRef.current?.focus?.();
        }

        return;
      }

      if (turnstileEnabled && !currentTurnstileToken) {
        submitLockRef.current = false;
        setError('Completa la verificación de seguridad.');
        return;
      }

      setError('');
      setLoading(true);

      try {
        try {
          delete api.defaults.headers.common.Authorization;
          delete api.defaults.headers.common.tenantid;
        } catch (_) {}

        // IMPORTANTE: el login no debe pasar por los interceptores globales
        // de la instancia Axios. Un 401 por contraseña incorrecta es un resultado
        // normal del formulario, no una sesión expirada. Usamos fetch directo
        // para evitar que un interceptor global haga logout, navigate o reload.
        const loginData = await fetchLoginDirect({
          email: normalizedLoginIdentifier,
          identifier: normalizedLoginIdentifier,
          login: normalizedLoginIdentifier,
          password,
          turnstileToken: currentTurnstileToken || undefined,
        });

        const newToken = loginData?.token;

        if (!newToken) {
          throw new Error('TOKEN_MISSING');
        }

        api.defaults.headers.common.Authorization = `Bearer ${newToken}`;

        const meData = await fetchAuthMeWithToken(newToken);

        const allowed = Array.isArray(meData?.allowedTenants)
          ? meData.allowedTenants
          : [];

        const meUser = meData?.user || null;

        setToken(newToken);
        setUser(meUser);
        setTenants(allowed);
        setTenantSearch('');

        loginValueRef.current = normalizedLoginIdentifier;

        if (rememberMe) {
          localStorage.setItem(
            'rememberedLogin',
            normalizedLoginIdentifier
          );

          localStorage.setItem(
            'rememberedEmail',
            normalizedLoginIdentifier
          );
        } else {
          localStorage.removeItem('rememberedLogin');
          localStorage.removeItem('rememberedEmail');
        }

        if (!allowed.length) {
          resetTurnstile();
          setError(
            'Tu usuario no tiene empresas asignadas. Contacta al administrador.'
          );
          return;
        }

        if (allowed.length === 1) {
          const soloTenant = normalizeTenant(allowed[0]);

          commitAuthSession({
            token: newToken,
            user: meUser,
            tenant: soloTenant,
          });

          onLogin?.({
            user: meUser,
            token: newToken,
            tenant: soloTenant,
          });

          return;
        }

        setSelectedTenantObj(null);
        setStep('tenant');
      } catch (err) {
        console.error('[LoginForm] Error en login:', err);

        const status = Number(err?.response?.status || err?.status || 0);
        const serverMsg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.data?.error ||
          err?.data?.message ||
          '';

        const isCredentialError = [400, 401, 403].includes(status);

        // IMPORTANTE:
        // Una contraseña incorrecta es un error local del formulario.
        // No debemos limpiar la sesión global ni provocar redirecciones/re-montajes.
        // Sólo renovamos Turnstile, porque su token es de un solo uso.
        if (isCredentialError) {
          try {
            delete api.defaults.headers.common.Authorization;
            delete api.defaults.headers.common.tenantid;
          } catch (_) {}

          resetTurnstile();
          setError(serverMsg || 'Correo/RUT o contraseña incorrectos.');

          requestAnimationFrame(() => {
            syncCredentialRefs(formEl);
            passInputRef.current?.focus?.();
            passInputRef.current?.select?.();
          });

          return;
        }

        // Para errores no atribuibles a credenciales sí limpiamos sólo el estado
        // temporal de autenticación, sin borrar localStorage ni navegar.
        clearAuth({ clearStorage: false });
        resetTurnstile();

        setError(
          serverMsg ||
            'No fue posible iniciar sesión. Verifica tu conexión e intenta nuevamente.'
        );

        requestAnimationFrame(() => {
          syncCredentialRefs(formEl);
          passInputRef.current?.focus?.();
        });
      } finally {
        setLoading(false);
        submitLockRef.current = false;
      }
    },
    [
      rememberMe,
      turnstileEnabled,
      clearAuth,
      getLiveCredentials,
      getCurrentTurnstileToken,
      onLogin,
      resetTurnstile,
      syncCredentialRefs,
      commitAuthSession,
    ]
  );

  const confirmTenant = useCallback(
    async (tenantToUse = null) => {
      if (loading) return;

      const chosenTenant = normalizeTenant(
        tenantToUse || selectedTenantObj || {}
      );

      if (!chosenTenant?.tenantid) {
        setError('Seleccione una empresa para continuar.');
        return;
      }

      if (!token) {
        setError('La sesión temporal expiró. Ingrese nuevamente.');
        goToLogin({
          clearStoredSession: switchTenantMode,
        });
        return;
      }

      setError('');
      setLoading(true);
      setLoadingTenantId(chosenTenant.tenantid);
      setSelectedTenantObj(chosenTenant);

      try {
        commitAuthSession({
          token,
          user,
          tenant: chosenTenant,
        });

        onLogin?.({
          user,
          token,
          tenant: chosenTenant,
        });
      } catch (err) {
        console.error(
          '[LoginForm] Error al seleccionar empresa:',
          err
        );

        setError('Error al seleccionar la empresa.');
      } finally {
        setLoading(false);
        setLoadingTenantId('');
      }
    },
    [
      loading,
      selectedTenantObj,
      token,
      user,
      onLogin,
      goToLogin,
      commitAuthSession,
      switchTenantMode,
    ]
  );

  const handleTenantSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      await confirmTenant();
    },
    [confirmTenant]
  );

  const renderMobileBrand = () => (
    <Box sx={sx.mobileBrand(theme)}>
      <Box
        component="img"
        src={logoHorizontal}
        alt="IntegraNube"
        sx={sx.mobileLogo(theme)}
      />

      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', fontWeight: 800 }}
      >
        ERP Cloud · Chile
      </Typography>
    </Box>
  );

  const renderBrandPanel = () => (
    <Box sx={sx.brandPanel(theme)}>
      <Stack sx={{ height: '100%' }}>
        <Box sx={sx.logoBox(theme)}>
          <Box
            component="img"
            src={logoHorizontal}
            alt="IntegraNube"
            sx={sx.logoImg(theme)}
          />
        </Box>

        <Box sx={{ mt: { md: 5, lg: 6 }, maxWidth: 310 }}>
          <Typography variant="h4" sx={sx.brandTitle(theme)}>
            Tu empresa, en un solo lugar.
          </Typography>

          <Typography variant="body1" sx={sx.brandSubtitle(theme)}>
            Gestión empresarial simple, segura y disponible desde cualquier lugar.
          </Typography>
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        <Stack spacing={0.45}>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.72)', fontWeight: 800 }}
          >
            Acceso seguro · IntegraNube Cloud
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.48)' }}
          >
            © {new Date().getFullYear()} IntegraNube
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );

  const loginButtonDisabled =
    loading || (turnstileEnabled && !turnstileToken);

  const renderLoginStep = () => (
    <Box sx={{ p: { xs: 2.2, sm: 3.3, md: 3.6 } }}>
      {renderMobileBrand()}

      <Stack spacing={0.9} sx={{ mb: 2.2 }}>
        <Box sx={sx.stepPill(theme)}>
          <Stack direction="row" spacing={0.9} alignItems="center">
            <LoginIcon fontSize="small" />

            <Typography
              variant="caption"
              sx={{ fontWeight: 900 }}
            >
              Acceso seguro
            </Typography>
          </Stack>
        </Box>

        <Typography variant="h5" sx={sx.formTitle(theme)}>
          Bienvenido a IntegraNube
        </Typography>

        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', lineHeight: 1.55 }}
        >
          Ingresa con correo o RUT para acceder a las empresas
          asignadas a tu cuenta.
        </Typography>
      </Stack>

      <Box
        ref={formRef}
        component="form"
        onSubmit={handleLoginSubmit}
        autoComplete="on"
        noValidate
      >
        <Stack spacing={1.6}>
          <TextField
            id="login-identifier"
            name="email"
            label="Correo o RUT"
            placeholder="usuario@suempresa.cl o 12345678-9"
            type="text"
            defaultValue={initialLogin}
            disabled={loading}
            inputRef={loginInputRef}
            inputProps={{
              autoComplete: 'username',
              inputMode: 'text',
              autoCapitalize: 'none',
              spellCheck: 'false',
            }}
            onChange={(e) => {
              loginValueRef.current = displayLoginIdentifier(
                e.target.value
              );
            }}
            onInput={(e) => {
              loginValueRef.current = displayLoginIdentifier(
                e.target.value
              );
            }}
            onBlur={(e) => {
              const normalized = normalizeLoginIdentifier(
                e.target.value
              );

              loginValueRef.current = normalized;

              if (normalized && e.target.value !== normalized) {
                e.target.value = normalized;
              }
            }}
            onFocus={() => {
              requestAnimationFrame(() => syncCredentialRefs());
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const currentPass = String(
                  passInputRef.current?.value ||
                    passValueRef.current ||
                    ''
                );

                if (!currentPass) {
                  e.preventDefault();
                  passInputRef.current?.focus?.();
                }
              }
            }}
            helperText="Ejemplo RUT: 12345678-9"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            id="login-password"
            name="password"
            label="Contraseña"
            type="password"
            defaultValue=""
            disabled={loading}
            inputRef={passInputRef}
            inputProps={{
              autoComplete: 'current-password',
            }}
            onChange={(e) => {
              passValueRef.current = String(e.target.value || '');
            }}
            onInput={(e) => {
              passValueRef.current = String(e.target.value || '');
            }}
            onBlur={(e) => {
              passValueRef.current = String(e.target.value || '');
            }}
            onFocus={() => {
              requestAnimationFrame(() => syncCredentialRefs());
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {turnstileEnabled ? (
            <Box sx={sx.securityPanel(theme, turnstileStatus)}>
              {turnstileStatus === 'error' && turnstileIssue ? (
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ width: '100%' }}
                >
                  <Stack direction="row" spacing={1.1} alignItems="center">
                    <Box sx={sx.securityIcon(theme, 'error')}>
                      <VerifiedUserIcon fontSize="small" />
                    </Box>

                    <Box>
                      <Typography sx={sx.securityTitle(theme)}>
                        Verificación no disponible
                      </Typography>
                      <Typography sx={sx.securitySubtitle(theme)}>
                        {turnstileIssue.message}
                      </Typography>
                    </Box>
                  </Stack>

                  <Button
                    type="button"
                    size="small"
                    variant="text"
                    onClick={retryTurnstileNow}
                    disabled={loading}
                    sx={sx.securityRetryBtn(theme)}
                  >
                    Reintentar
                  </Button>
                </Stack>
              ) : null}

              <Box sx={sx.captchaBox(theme)}>
                <TurnstileWidget
                  ref={turnstileRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  action="login"
                  theme="auto"
                  onLoading={(phase) => {
                    setTurnstileToken('');
                    setTurnstileStatus(
                      phase === 'retrying' ? 'retrying' : 'loading'
                    );
                  }}
                  onReady={() => {
                    setTurnstileReady(true);
                    setTurnstileStatus((current) =>
                      current === 'verified' ? 'verified' : 'ready'
                    );
                  }}
                  onToken={(value) => {
                    const nextToken = String(value || '').trim();
                    clearTurnstileAutoRetry();
                    turnstileAutoRetryRef.current = 0;
                    setTurnstileToken(nextToken);
                    setTurnstileIssue(null);
                    setTurnstileStatus(nextToken ? 'verified' : 'ready');
                    setError('');
                  }}
                  onExpired={() => {
                    setTurnstileToken('');
                    setTurnstileIssue({
                      code: 'TOKEN_EXPIRED',
                      retryable: true,
                      kind: 'expired',
                      message:
                        'La verificación expiró y se está renovando automáticamente.',
                    });
                    setTurnstileStatus('retrying');
                  }}
                  onTimeout={() => {
                    setTurnstileToken('');
                    setTurnstileIssue({
                      code: 'CHALLENGE_TIMEOUT',
                      retryable: true,
                      kind: 'timeout',
                      message:
                        'La verificación tardó demasiado y se está actualizando.',
                    });
                    setTurnstileStatus('retrying');
                  }}
                  onError={(code) => {
                    const issue = classifyTurnstileError(code);

                    console.warn('[LoginForm] Turnstile error:', {
                      code: issue.code,
                      kind: issue.kind,
                      retryable: issue.retryable,
                      hostname: window.location.hostname,
                      online: navigator.onLine,
                    });

                    setTurnstileToken('');
                    setTurnstileIssue(issue);
                    setTurnstileStatus(issue.retryable ? 'retrying' : 'error');

                    const scriptLevelFailure =
                      issue.code === 'TURNSTILE_SCRIPT_LOAD_FAILED' ||
                      issue.code === 'TURNSTILE_SCRIPT_TIMEOUT' ||
                      issue.code === 'TURNSTILE_API_NOT_AVAILABLE';

                    if (
                      issue.retryable &&
                      scriptLevelFailure &&
                      turnstileAutoRetryRef.current < 2
                    ) {
                      clearTurnstileAutoRetry();
                      turnstileAutoRetryRef.current += 1;
                      turnstileAutoRetryTimerRef.current = window.setTimeout(
                        () => {
                          turnstileRef.current?.reload?.();
                        },
                        2500 * turnstileAutoRetryRef.current
                      );
                    }
                  }}
                />
              </Box>

            </Box>
          ) : (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              Turnstile no configurado. Falta
              {' REACT_APP_TURNSTILE_SITE_KEY.'}
            </Alert>
          )}

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={loading}
                />
              }
              label="Recordar usuario"
            />

            <Typography
              variant="caption"
              sx={{ color: 'text.secondary' }}
            >
              soporte@integranube.cl
            </Typography>
          </Stack>

          {error ? (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loginButtonDisabled}
            startIcon={loading ? null : <LoginIcon />}
            sx={sx.primaryBtn(theme)}
            fullWidth
            onPointerDown={() => syncCredentialRefs()}
            onMouseDown={() => syncCredentialRefs()}
            onTouchStart={() => syncCredentialRefs()}
            onFocus={() => syncCredentialRefs()}
          >
            {loading ? (
              <Stack
                direction="row"
                spacing={1.2}
                alignItems="center"
              >
                <CircularProgress size={20} color="inherit" />
                <span>Validando…</span>
              </Stack>
            ) : turnstileEnabled && !turnstileToken ? (
              'Completa la verificación para continuar'
            ) : (
              'Iniciar sesión'
            )}
          </Button>

          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              lineHeight: 1.45,
            }}
          >
            Si la cuenta tiene acceso a más de una empresa, podrás
            elegir con cuál operar en el siguiente paso.
          </Typography>
        </Stack>
      </Box>
    </Box>
  );

  const renderTenantCard = (tenant) => {
    const selected = selectedTenantObj?.tenantid === tenant.tenantid;
    const loadingThis =
      loading && loadingTenantId === tenant.tenantid;

    const role = tenant._role || 'Usuario';
    const status = tenant._status;
    const rut = tenant._rut;
    const legalName = tenant._legalName;

    return (
      <Card
        key={tenant.tenantid}
        elevation={0}
        sx={sx.tenantCard(theme, selected, loadingThis)}
      >
        <CardActionArea
          component="div"
          role="button"
          tabIndex={0}
          disabled={loading}
          onClick={() => confirmTenant(tenant)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              confirmTenant(tenant);
            }
          }}
          sx={{ height: '100%' }}
        >
          <CardContent
            sx={{
              p: { xs: 1.35, sm: 2 },
              height: '100%',
            }}
          >
            <Stack
              spacing={{ xs: 1, sm: 1.4 }}
              sx={{ height: '100%' }}
            >
              <Stack
                direction="row"
                spacing={1.4}
                alignItems="flex-start"
              >
                <Avatar sx={sx.tenantAvatar(theme, selected)}>
                  {tenant._initials || <BusinessRoundedIcon />}
                </Avatar>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack
                    direction="row"
                    spacing={0.8}
                    alignItems="center"
                    sx={{
                      mb: 0.55,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Chip
                      size="small"
                      icon={<AdminPanelSettingsRoundedIcon />}
                      label={role}
                      sx={sx.tenantRoleChip(theme, selected)}
                    />

                    {status ? (
                      <Chip
                        size="small"
                        label={status}
                        sx={sx.tenantStatusChip(theme)}
                      />
                    ) : null}
                  </Stack>

                  <Typography
                    variant="subtitle1"
                    sx={sx.tenantName(theme)}
                    title={tenant._name}
                  >
                    {tenant._name}
                  </Typography>

                  {legalName ? (
                    <Typography
                      variant="caption"
                      sx={sx.tenantMeta(theme)}
                      title={legalName}
                    >
                      {legalName}
                    </Typography>
                  ) : null}

                  {rut ? (
                    <Typography
                      variant="caption"
                      sx={sx.tenantMeta(theme)}
                    >
                      RUT {rut}
                    </Typography>
                  ) : null}
                </Box>

                <Box
                  sx={sx.tenantAction(
                    theme,
                    selected,
                    loadingThis
                  )}
                >
                  {loadingThis ? (
                    <CircularProgress size={19} color="inherit" />
                  ) : selected ? (
                    <CheckCircleRoundedIcon fontSize="small" />
                  ) : (
                    <ArrowForwardRoundedIcon fontSize="small" />
                  )}
                </Box>
              </Stack>

              <Box sx={{ flexGrow: 1 }} />

              <Stack
                direction="row"
                spacing={0.8}
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack
                  direction="row"
                  spacing={0.8}
                  alignItems="center"
                  sx={{ minWidth: 0 }}
                >
                  <StorefrontRoundedIcon
                    sx={{
                      fontSize: 17,
                      color: 'text.secondary',
                    }}
                  />

                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary' }}
                    noWrap
                  >
                    Empresa habilitada
                  </Typography>
                </Stack>

                <Typography
                  variant="caption"
                  sx={sx.enterLabel(theme, selected)}
                >
                  Entrar
                </Typography>
              </Stack>
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
    );
  };

  const renderTenantStep = () => (
    <Box sx={{ p: { xs: 2.1, sm: 3.2, md: 3.8 } }}>
      {renderMobileBrand()}

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'flex-start' }}
        justifyContent="space-between"
        sx={{ mb: { xs: 2, md: 2.4 } }}
      >
        <Stack spacing={0.9} sx={{ maxWidth: 560 }}>
          <Box sx={sx.stepPill(theme)}>
            <Stack direction="row" spacing={0.9} alignItems="center">
              <TenantIcon fontSize="small" />

              <Typography
                variant="caption"
                sx={{ fontWeight: 900 }}
              >
                Selección de empresa
              </Typography>
            </Stack>
          </Box>

          <Typography variant="h5" sx={sx.formTitle(theme)}>
            Seleccione la empresa con la que desea operar
          </Typography>

          <Typography
            variant="body2"
            sx={{
              display: { xs: 'block', md: 'none' },
              color: 'text.secondary',
              fontWeight: 700,
              lineHeight: 1.45,
            }}
          >
            Toca una empresa para entrar directamente.
          </Typography>
        </Stack>

        <Box sx={sx.userStrip(theme)}>
          <Stack spacing={0.2} sx={{ minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 800,
              }}
            >
              Usuario conectado
            </Typography>

            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 950 }}
              noWrap
            >
              {safeUserLabel(user)}
            </Typography>

            <Typography
              variant="caption"
              sx={{ color: 'text.secondary' }}
              noWrap
            >
              {String(
                loginValueRef.current ||
                  user?.email ||
                  user?.rut ||
                  ''
              )}
            </Typography>
          </Stack>

          <Button
            size="small"
            variant="outlined"
            onClick={() =>
              goToLogin({
                clearStoredSession: switchTenantMode,
              })
            }
            disabled={loading}
            startIcon={<ArrowBackIcon />}
            sx={{
              fontWeight: 900,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              borderRadius: 2,
            }}
            type="button"
          >
            Cambiar cuenta
          </Button>
        </Box>
      </Stack>

      <Box component="form" onSubmit={handleTenantSubmit}>
        <Stack spacing={1.6}>
          {tenantOptions.length > 4 ? (
            <TextField
              value={tenantSearch}
              onChange={(e) => setTenantSearch(e.target.value)}
              disabled={loading}
              placeholder="Buscar empresa por nombre, RUT o rol…"
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          ) : null}

          {error ? (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          ) : null}

          {loading && !tenantOptions.length ? (
            <Box sx={sx.loadingTenants(theme)}>
              <CircularProgress />

              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 800,
                }}
              >
                Cargando empresas disponibles…
              </Typography>
            </Box>
          ) : filteredTenantOptions.length ? (
            <Box sx={sx.tenantGrid(theme)}>
              {filteredTenantOptions.map(renderTenantCard)}
            </Box>
          ) : (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              No se encontraron empresas con ese criterio de búsqueda.
            </Alert>
          )}

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            sx={{
              pt: 0.5,
              display: { xs: 'none', md: 'flex' },
            }}
          >
            <Button
              type="button"
              variant="outlined"
              onClick={() =>
                goToLogin({
                  clearStoredSession: switchTenantMode,
                })
              }
              disabled={loading}
              startIcon={<ArrowBackIcon />}
              sx={sx.secondaryBtn(theme)}
            >
              {switchTenantMode ? 'Usar otra cuenta' : 'Volver'}
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={loading || !selectedTenantObj?.tenantid}
              startIcon={loading ? null : <LoginIcon />}
              sx={sx.primaryBtn(theme)}
            >
              {loading ? (
                <Stack
                  direction="row"
                  spacing={1.2}
                  alignItems="center"
                >
                  <CircularProgress size={20} color="inherit" />
                  <span>Entrando…</span>
                </Stack>
              ) : selectedTenantObj?.tenantid ? (
                'Continuar con empresa seleccionada'
              ) : (
                'Seleccione una empresa para continuar'
              )}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );

  return (
    <Box sx={sx.root(theme, backgroundImage)}>
      <ServedLocationFlag />

      <Container
        maxWidth={step === 'tenant' ? 'lg' : 'md'}
        sx={{ position: 'relative', zIndex: 1 }}
      >
        <Paper elevation={0} sx={sx.shell(theme, step)}>
          <Grid
            container
            sx={{
              minHeight: {
                xs: 'auto',
                md: step === 'tenant' ? 620 : 540,
              },
            }}
          >
            <Grid
              item
              md={4}
              sx={{ display: { xs: 'none', md: 'block' } }}
            >
              {renderBrandPanel()}
            </Grid>

            <Grid
              item
              xs={12}
              md={8}
              sx={sx.formPanel(theme)}
            >
              {step === 'login'
                ? renderLoginStep()
                : renderTenantStep()}
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
}

const sx = {
  root: (theme, bg) => ({
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    py: { xs: 2.2, sm: 3.5, md: 6 },
    px: { xs: 1.3, sm: 0 },
    position: 'relative',
    backgroundImage: `
      radial-gradient(1000px circle at 18% 12%, rgba(255,255,255,0.10), transparent 55%),
      radial-gradient(900px circle at 86% 22%, rgba(255,255,255,0.08), transparent 55%),
      linear-gradient(rgba(7,11,19,0.72), rgba(7,11,19,0.88)),
      url(${bg})
    `,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      background:
        'linear-gradient(90deg, rgba(0,0,0,0.32) 0%, rgba(0,0,0,0.00) 55%, rgba(0,0,0,0.20) 100%)',
      pointerEvents: 'none',
    },
  }),

  shell: (theme, step) => ({
    borderRadius: { xs: 3.2, sm: 4.5 },
    overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.12)',
    backgroundColor: 'rgba(255,255,255,0.96)',
    boxShadow: '0 28px 80px rgba(0,0,0,0.50)',
    backdropFilter: 'blur(14px)',
    maxWidth: step === 'tenant' ? 1120 : 860,
    mx: 'auto',
  }),

  brandPanel: () => ({
    height: '100%',
    p: { md: 3.5, lg: 4 },
    color: '#fff',
    position: 'relative',
    backgroundImage: `
      radial-gradient(1200px circle at 0% 0%, rgba(255,255,255,0.13), transparent 55%),
      radial-gradient(900px circle at 100% 100%, rgba(0,120,255,0.20), transparent 55%),
      linear-gradient(180deg, rgba(10,16,28,0.96), rgba(10,16,28,0.88))
    `,
    borderRight: '1px solid rgba(255,255,255,0.10)',
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      backgroundImage:
        'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.05) 1px, transparent 0)',
      backgroundSize: '14px 14px',
      opacity: 0.75,
      pointerEvents: 'none',
    },
    '& > *': {
      position: 'relative',
      zIndex: 1,
    },
  }),

  mobileBrand: () => ({
    display: { xs: 'flex', md: 'none' },
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 2,
    mb: 2.1,
    pb: 1.7,
    borderBottom: '1px solid rgba(0,0,0,0.08)',
  }),

  mobileLogo: () => ({
    width: 178,
    maxWidth: '62%',
    height: 'auto',
    display: 'block',
  }),

  logoBox: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    padding: theme.spacing(0.5, 0),
  }),

  logoImg: () => ({
    width: '100%',
    maxWidth: 260,
    height: 'auto',
    display: 'block',
    filter: 'drop-shadow(0 10px 18px rgba(0,0,0,0.55))',
    WebkitFilter:
      'drop-shadow(0 10px 18px rgba(0,0,0,0.55))',
  }),

  brandTitle: () => ({
    fontWeight: 950,
    letterSpacing: -0.5,
    lineHeight: 1.08,
    color: 'rgba(255,255,255,0.98)',
  }),

  brandSubtitle: () => ({
    mt: 1.5,
    color: 'rgba(255,255,255,0.70)',
    fontWeight: 600,
    lineHeight: 1.55,
  }),

  valuesLabel: (theme) => ({
    fontSize: 12,
    letterSpacing: 1.2,
    fontWeight: 950,
    color: 'rgba(255,255,255,0.70)',
    marginBottom: theme.spacing(1.2),
  }),

  valueList: () => ({
    borderRadius: 2.5,
    overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.12)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
  }),

  valueItem: (theme, last) => ({
    display: 'flex',
    gap: 1.4,
    alignItems: 'flex-start',
    padding: theme.spacing(1.25, 1.4),
    borderBottom: last
      ? 'none'
      : '1px solid rgba(255,255,255,0.10)',
  }),

  valueIcon: (theme) => ({
    width: 38,
    height: 38,
    borderRadius: 12,
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: alpha(theme.palette.primary.main, 0.18),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.30)}`,
    boxShadow: `0 10px 20px ${alpha(
      theme.palette.primary.main,
      0.10
    )}`,
    '& svg': {
      fontSize: 19,
      color: 'rgba(255,255,255,0.92)',
    },
  }),

  valueTitle: () => ({
    color: 'rgba(255,255,255,0.95)',
    fontWeight: 900,
    letterSpacing: 0.1,
  }),

  valueDesc: () => ({
    color: 'rgba(255,255,255,0.70)',
    lineHeight: 1.25,
    marginTop: 0.25,
  }),

  formPanel: () => ({
    backgroundColor: 'rgba(255,255,255,0.985)',
  }),

  stepPill: (theme) => ({
    px: 1.25,
    py: 0.65,
    borderRadius: 999,
    backgroundColor: alpha(theme.palette.primary.main, 0.075),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
    color: theme.palette.primary.main,
    width: 'fit-content',
  }),

  formTitle: () => ({
    fontWeight: 950,
    letterSpacing: '-0.035em',
    lineHeight: 1.08,
  }),

  primaryBtn: () => ({
    borderRadius: 2,
    py: 1.15,
    fontWeight: 950,
    letterSpacing: 0.2,
    textTransform: 'none',
    boxShadow: 'none',
  }),

  secondaryBtn: () => ({
    borderRadius: 2,
    py: 1.05,
    fontWeight: 900,
    textTransform: 'none',
  }),

  userStrip: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: { xs: 1.2, sm: 2 },
    p: { xs: 1.15, sm: 1.35 },
    borderRadius: 2.5,
    backgroundColor: alpha(theme.palette.primary.main, 0.045),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
    minWidth: { xs: '100%', md: 280 },
  }),

  securityPanel: (theme, status) => ({
    p: { xs: 1.2, sm: 1.4 },
    borderRadius: 2.5,
    border: `1px solid ${
      status === 'verified'
        ? alpha(theme.palette.success.main, 0.24)
        : status === 'error'
          ? alpha(theme.palette.error.main, 0.26)
          : alpha(theme.palette.primary.main, 0.14)
    }`,
    backgroundColor:
      status === 'verified'
        ? alpha(theme.palette.success.main, 0.035)
        : status === 'error'
          ? alpha(theme.palette.error.main, 0.03)
          : alpha(theme.palette.primary.main, 0.025),
    transition: 'border-color 180ms ease, background-color 180ms ease',
  }),

  securityIcon: (theme, status) => ({
    width: 34,
    height: 34,
    flex: '0 0 auto',
    borderRadius: 2,
    display: 'grid',
    placeItems: 'center',
    color:
      status === 'verified'
        ? theme.palette.success.main
        : status === 'error'
          ? theme.palette.error.main
          : theme.palette.primary.main,
    backgroundColor:
      status === 'verified'
        ? alpha(theme.palette.success.main, 0.1)
        : status === 'error'
          ? alpha(theme.palette.error.main, 0.1)
          : alpha(theme.palette.primary.main, 0.08),
  }),

  securityTitle: () => ({
    fontSize: 13,
    lineHeight: 1.25,
    fontWeight: 900,
  }),

  securitySubtitle: () => ({
    mt: 0.2,
    fontSize: 11.5,
    lineHeight: 1.35,
    color: 'text.secondary',
  }),

  securityRetryBtn: () => ({
    minWidth: 'auto',
    px: 1,
    fontSize: 11.5,
    fontWeight: 900,
    textTransform: 'none',
    whiteSpace: 'nowrap',
  }),

  securityCode: () => ({
    display: 'block',
    mt: 0.6,
    fontSize: 10.5,
    lineHeight: 1.25,
    color: 'text.disabled',
  }),

  captchaBox: () => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    minHeight: 70,
    mt: 1.1,
    borderRadius: 2,
    backgroundColor: 'rgba(255,255,255,0.76)',
    border: '1px solid rgba(0,0,0,0.06)',
    overflow: 'hidden',
    px: { xs: 0.5, sm: 1 },
    py: 0.65,
  }),

  tenantGrid: () => ({
    display: 'grid',
    gridTemplateColumns: {
      xs: '1fr',
      sm: 'repeat(2, minmax(0, 1fr))',
      lg: 'repeat(2, minmax(0, 1fr))',
    },
    gap: { xs: 1.35, sm: 1.35 },
    maxHeight: {
      xs: 'calc(100dvh - 310px)',
      md: 390,
    },
    minHeight: { xs: 0, md: 'auto' },
    overflowY: 'auto',
    pr: { xs: 0.4, md: 0.8 },
    pb: { xs: 2.5, md: 0.2 },
    overscrollBehavior: 'contain',
    WebkitOverflowScrolling: 'touch',
    '&::-webkit-scrollbar': {
      width: 8,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(0,0,0,0.18)',
      borderRadius: 999,
    },
  }),

  tenantCard: (theme, selected, loadingThis) => ({
    height: '100%',
    minHeight: {
      xs: 132,
      sm: 166,
    },
    borderRadius: {
      xs: 2.5,
      sm: 3.2,
    },
    overflow: 'hidden',
    position: 'relative',
    border: `1px solid ${
      selected
        ? alpha(theme.palette.primary.main, 0.55)
        : alpha(theme.palette.divider, 0.92)
    }`,
    background:
      selected || loadingThis
        ? `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.11
          )}, ${alpha(theme.palette.primary.main, 0.025)})`
        : `linear-gradient(135deg, ${
            theme.palette.background.paper
          }, ${alpha(theme.palette.primary.main, 0.025)})`,
    boxShadow:
      selected || loadingThis
        ? `0 18px 44px ${alpha(theme.palette.primary.main, 0.18)}`
        : `0 12px 34px ${alpha(theme.palette.common.black, 0.055)}`,
    transition:
      'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
    '&::after': {
      content: '""',
      position: 'absolute',
      width: {
        xs: 96,
        sm: 130,
      },
      height: {
        xs: 96,
        sm: 130,
      },
      right: {
        xs: -42,
        sm: -58,
      },
      bottom: {
        xs: -52,
        sm: -70,
      },
      borderRadius: '50%',
      backgroundColor: alpha(
        theme.palette.primary.main,
        selected ? 0.13 : 0.055
      ),
      pointerEvents: 'none',
    },
    '&:hover': {
      transform: loadingThis ? 'none' : 'translateY(-2px)',
      borderColor: alpha(theme.palette.primary.main, 0.52),
      boxShadow: `0 20px 52px ${alpha(
        theme.palette.primary.main,
        0.16
      )}`,
    },
  }),

  tenantAvatar: (theme, selected) => ({
    width: { xs: 44, sm: 50 },
    height: { xs: 44, sm: 50 },
    borderRadius: 2.4,
    fontWeight: 950,
    letterSpacing: '-0.03em',
    color: selected
      ? theme.palette.primary.contrastText
      : theme.palette.primary.main,
    backgroundColor: selected
      ? theme.palette.primary.main
      : alpha(theme.palette.primary.main, 0.11),
    border: `1px solid ${alpha(
      theme.palette.primary.main,
      selected ? 0.2 : 0.16
    )}`,
    boxShadow: selected
      ? `0 12px 24px ${alpha(theme.palette.primary.main, 0.24)}`
      : 'none',
  }),

  tenantRoleChip: (theme, selected) => ({
    height: 24,
    borderRadius: 999,
    fontWeight: 900,
    fontSize: 11,
    color: selected
      ? theme.palette.primary.main
      : theme.palette.text.secondary,
    backgroundColor: selected
      ? alpha(theme.palette.primary.main, 0.12)
      : alpha(theme.palette.text.primary, 0.055),
    '& .MuiChip-icon': {
      color: 'inherit',
      fontSize: 15,
    },
  }),

  tenantStatusChip: (theme) => ({
    height: 24,
    borderRadius: 999,
    fontWeight: 900,
    fontSize: 11,
    color: theme.palette.success.main,
    backgroundColor: alpha(theme.palette.success.main, 0.11),
  }),

  tenantName: () => ({
    fontWeight: 950,
    letterSpacing: '-0.025em',
    lineHeight: 1.18,
    fontSize: { xs: 18, sm: 20 },
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  }),

  tenantMeta: () => ({
    display: 'block',
    mt: 0.35,
    color: 'text.secondary',
    lineHeight: 1.25,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }),

  tenantAction: (theme, selected, loadingThis) => ({
    width: { xs: 34, sm: 36 },
    height: { xs: 34, sm: 36 },
    borderRadius: '50%',
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color:
      selected || loadingThis
        ? theme.palette.primary.contrastText
        : theme.palette.primary.main,
    backgroundColor:
      selected || loadingThis
        ? theme.palette.primary.main
        : alpha(theme.palette.primary.main, 0.1),
    boxShadow:
      selected || loadingThis
        ? `0 12px 24px ${alpha(theme.palette.primary.main, 0.22)}`
        : 'none',
  }),

  enterLabel: (theme, selected) => ({
    fontWeight: 950,
    color: selected
      ? theme.palette.primary.main
      : theme.palette.text.secondary,
  }),

  loadingTenants: () => ({
    minHeight: 220,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 3,
    border: '1px dashed rgba(0,0,0,0.16)',
    backgroundColor: 'rgba(0,0,0,0.025)',
  }),
};